// src/components/Toast.tsx — micro toast system, no deps
import React, { createContext, useCallback, useContext, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

export type ToastTone = 'info' | 'success' | 'wicket' | 'boundary' | 'milestone';
interface Toast {
  id: string;
  title: string;
  body?: string;
  tone?: ToastTone;
  ttl?: number;
}

interface ToastCtx {
  push: (t: Omit<Toast, 'id'>) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = crypto.randomUUID();
    const toast: Toast = { id, ttl: 3800, tone: 'info', ...t };
    setToasts(prev => [...prev, toast]);
    window.setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), toast.ttl);

    // Mirror to OS notification when tab is hidden + permission granted
    try {
      if (typeof Notification !== 'undefined' &&
          Notification.permission === 'granted' &&
          document.visibilityState === 'hidden' &&
          (toast.tone === 'wicket' || toast.tone === 'boundary' || toast.tone === 'milestone')) {
        const n = new Notification(toast.title, { body: toast.body, tag: 'criclocal-' + toast.tone, silent: false });
        window.setTimeout(() => { try { n.close(); } catch {} }, 5000);
      }
    } catch {}
  }, []);

  return (
    <Ctx.Provider value={{ push }}>
      {children}
      <div style={{ position: 'fixed', top: 80, right: 18, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320, pointerEvents: 'none' }}>
        <AnimatePresence>
          {toasts.map(t => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, x: 30, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 30, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              style={{
                background: 'var(--bg-card-solid)',
                border: `1px solid ${
                  t.tone === 'wicket' ? 'var(--signal-live)' :
                  t.tone === 'boundary' ? 'var(--brand-champagne)' :
                  t.tone === 'milestone' ? 'var(--brand-forest)' :
                  'var(--hairline)'
                }`,
                borderLeftWidth: 3,
                padding: '12px 16px',
                borderRadius: 'var(--radius-md)',
                boxShadow: '0 18px 48px rgba(0,0,0,0.35)',
                color: 'var(--text-primary)',
                pointerEvents: 'auto',
              }}
            >
              <div className="cl-mono" style={{
                fontSize: 10, letterSpacing: 1.3, fontWeight: 600,
                color: t.tone === 'wicket' ? 'var(--signal-live)' :
                       t.tone === 'boundary' ? 'var(--brand-champagne)' :
                       t.tone === 'milestone' ? 'var(--brand-forest)' :
                       'var(--text-muted)',
              }}>{t.tone?.toUpperCase() || 'INFO'}</div>
              <div className="cl-serif" style={{ fontSize: 15, fontWeight: 600, marginTop: 4, letterSpacing: -0.2 }}>{t.title}</div>
              {t.body && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4 }}>{t.body}</div>}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const c = useContext(Ctx);
  if (!c) return { push: () => {} };
  return c;
}

// Request OS Notification permission. Call once on user interaction.
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') return Notification.permission;
  try { return await Notification.requestPermission(); } catch { return 'denied'; }
}
